import type { Permission, StaffProfile } from '@mevaser/shared'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type * as React from 'react'
import { createContext, useCallback, useContext, useMemo } from 'react'
import { api } from './api.js'

interface MeResponse {
  staff: StaffProfile | null
  customer: unknown
  pending2fa: boolean
}

interface AuthContextValue {
  staff: StaffProfile | null
  pending2fa: boolean
  isLoading: boolean
  /** True while a re-check is in flight, even though stale data is available. */
  isRefreshing: boolean
  /** Permission check used by both routing and individual controls. */
  can: (permission: Permission) => boolean
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient()

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/auth/me'),
    // The session is the one thing worth re-checking on focus: an account
    // disabled by an administrator should not keep working in an open tab.
    refetchOnWindowFocus: true,
    retry: false,
    staleTime: 30_000,
  })

  const can = useCallback(
    (permission: Permission) => data?.staff?.permissions.includes(permission) ?? false,
    [data],
  )

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: ['me'] })
  }, [queryClient])

  const signOut = useCallback(async () => {
    await api.post('/auth/logout', {})
    // Clear everything: another user may sign in at this same terminal, and
    // cached customer data must not survive the handover.
    queryClient.clear()
    window.location.href = '/login'
  }, [queryClient])

  const value = useMemo<AuthContextValue>(
    () => ({
      staff: data?.staff ?? null,
      pending2fa: data?.pending2fa ?? false,
      isLoading,
      isRefreshing: isFetching,
      can,
      refresh,
      signOut,
    }),
    [data, isLoading, isFetching, can, refresh, signOut],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside an AuthProvider')
  return context
}
